import React, { useMemo } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from "@mui/material";
import { QrCode as QrCodeIcon } from "@mui/icons-material";
import { getCountryFlag } from "@/constants/country-flags";
import { formatHTSCode } from "@/utils";
import { tokens, FLAG_EMOJI_SIZE } from "@/tokens";

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  countries: Array<{
    country_code: string;
    total_goods_value: number;
    percentage?: number;
  }>;
  isLoading?: boolean;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  helperText?: string;
}

export const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  countries,
  isLoading = false,
  required = false,
  disabled = false,
  label = "Country of Origin",
  helperText = "Select from list or type a country code (e.g., CN, US, VN)",
}) => {
  const sortedCountries = useMemo(() => {
    if (!countries) return [];
    
    const totalValue = countries.reduce(
      (sum, country) => sum + country.total_goods_value, 
      0
    );

    return [...countries].sort(
      (a, b) => b.total_goods_value - a.total_goods_value
    ).map(country => ({
      ...country,
      percentage: country.percentage || (country.total_goods_value / totalValue) * 100
    }));
  }, [countries]);

  return (
    <Autocomplete
      freeSolo
      options={sortedCountries}
      loading={isLoading}
      disabled={disabled}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.country_code;
      }}
      value={sortedCountries.find(c => c.country_code === value) || value}
      onChange={(_, newValue) => {
        if (typeof newValue === 'string') {
          const cleanValue = newValue.substring(0, 2).toUpperCase();
          onChange(cleanValue);
        } else {
          onChange(newValue?.country_code || "");
        }
      }}
      onInputChange={(_, newInputValue) => {
        const cleanValue = newInputValue.substring(0, 2).toUpperCase();
        onChange(cleanValue);
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: FLAG_EMOJI_SIZE }}>{getCountryFlag(option.country_code)}</span>
          <span style={{ fontWeight: tokens.fontWeight.semibold }}>{option.country_code}</span>
          <Chip
            label={`${option.percentage?.toFixed(1)}%`}
            size="small"
            sx={{
              ml: 'auto',
              fontSize: tokens.fontSize.xs,
              fontWeight: tokens.fontWeight.bold,
              bgcolor: 'rgba(11,73,75,0.08)',
              color: tokens.brand.primary,
              height: 22,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: tokens.radius.md,
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: value && (
              <span style={{ marginRight: 8, fontSize: FLAG_EMOJI_SIZE }}>
                {getCountryFlag(value)}
              </span>
            ),
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

interface HTSCodeSelectProps {
  value: string;
  onChange: (value: string) => void;
  htsCodes: Array<{
    hts_code: string;
    goods_value: number;
    duty_value?: number;
  }>;
  isLoading?: boolean;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  helperText?: string;
}

export const HTSCodeSelect: React.FC<HTSCodeSelectProps> = ({
  value,
  onChange,
  htsCodes,
  isLoading = false,
  required = false,
  disabled = false,
  label = "HTS Code",
  helperText = "Select from list or type an HTS code (e.g., 8517.12.00)",
}) => {
  const sortedHTSCodes = useMemo(() => {
    if (!htsCodes) return [];
    
    return [...htsCodes].sort(
      (a, b) => b.goods_value - a.goods_value
    ).map(code => ({
      ...code,
      formatted_hts_code: formatHTSCode(code.hts_code)
    }));
  }, [htsCodes]);

  return (
    <Autocomplete
      freeSolo
      options={sortedHTSCodes}
      loading={isLoading}
      disabled={disabled}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.formatted_hts_code;
      }}
      value={sortedHTSCodes.find(c => c.formatted_hts_code === value) || value}
      onChange={(_, newValue) => {
        if (typeof newValue === 'string') {
          // Format the HTS code when typed
          const formattedValue = formatHTSCode(newValue.replace(/\D/g, ""));
          onChange(formattedValue);
        } else {
          onChange(newValue?.formatted_hts_code || "");
        }
      }}
      onInputChange={(_, newInputValue) => {
        // Format the HTS code when typed
        const formattedValue = formatHTSCode(newInputValue.replace(/\D/g, ""));
        onChange(formattedValue);
      }}
      ListboxProps={{
        style: { maxHeight: 240 },
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, minHeight: 0 }}>
          <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ fontSize: tokens.fontSize.md }}>
            {option.formatted_hts_code}
          </Typography>
          <Chip
            label={`$${(option.goods_value / 1000000).toFixed(1)}M`}
            size="small"
            sx={{
              ml: 'auto',
              fontSize: tokens.fontSize.xs,
              fontWeight: tokens.fontWeight.bold,
              bgcolor: 'rgba(11,73,75,0.08)',
              color: tokens.brand.primary,
              height: 22,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: tokens.radius.md,
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <QrCodeIcon sx={{ color: tokens.text.secondary, mr: 1, fontSize: 20 }} />
            ),
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}; 